import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import ReactModal from "react-modal";
import {
  Alert,
  Collapsible,
  Money,
  Separator,
  FieldGroup,
} from "../../components";
import { Button, ButtonGroup } from "../../components/ui";
import { PAYMENT_DIALOG, STEPS } from "../constants";
import { ExpertAccountSelect, PaymentRow } from "../atoms";
import { MANUAL_PAYMENT_METHODS } from "../paymentMethods";
import { SwitchXeroOrgModal, orgLabel } from "../atoms/SwitchXeroOrgModal";
import { TaxRate } from "./TaxRate";
import { useOrderManagement } from "../useOrderManagement";
import { SettingsContext, UserContext } from "../../contexts";
import { canAdminAccess, ACCESS } from "../../helpers/adminAccess";
import { useStoreWriteGuard } from "../../helpers/storeScope";
import { useRemovePayment } from "../../cart/orderManagementApi";
import { SectionTrigger } from "./SectionTrigger";
import { XeroOrgSelect } from "./XeroOrgSelect";
import { XeroAddressSelect } from "./XeroAddressSelect";
import { XeroBrandingSelect } from "./XeroBrandingSelect";
import { formatMoney, symbolFor } from "../../core/currency";
import FxUnavailable from "../../components/formatters/FxUnavailable";

import "./payments.scss";
import { MarketplaceFeeType } from "../atoms/MarketplaceFeeType";

export function Payments({
  step,
  lastStep,
  isPxOrder,
  deposits,
  updateDepositField,
  removeDepositRow,
  taxRates,
  updateTaxRate,
  totalRemainingPaid,
  setTotalRemainingPaidField,
  listings,
  sellRequests,
  quotes,
  paidAmount,
  populatedAmount,
  fees,
  toPay,
  orderPaid,
  allPaymentsConfirmed,
  arePaymentsValid,
  showPaymentErrors,
  savePayments,
  goToInvoice,
  paymentDialog,
  updatePaymentDialog,
  marketplaceFee,
  setMarketplaceFee,
  marketplaceFeeType,
  setMarketplaceFeeType,
}) {
  const {
    isInline,
    order,
    setStep,
    createNewUnpaidOrder,
    // FX RULE (hooks/fx.js): the totals below are converted figures. With no
    // known rate they show "Rate unavailable" instead of a guessed number.
    fxMissing,
    fxUnavailable,
    // Order currency, resolved from the quote (not the listing). All order
    // totals below are computed against this as the FX base, so they must be
    // labelled with it too — otherwise <Money> falls back to GBP.
    listingRate,
    // The currency a listing's OWN price is denominated in (NULL ⇒ legacy GBP).
    // NOT the deal currency — see the Listing heading below.
    listingPriceCurrency,
    // Payment rows in their ORIGINAL currency (no FX applied).
    paymentRows,
    // The exact amount UPDATE LISTING PRICE writes (minor units, listingRate).
    // Read from the hook — NOT recomputed here — so the confirmation cannot
    // promise a figure other than the one the PUT sends.
    newListingPrice,
    // EXPERT FEES — the order's one optional outgoing fee. Read straight off
    // the hook rather than threaded through props: it is not part of the
    // payment payload the parent assembles, and must not look like it is.
    expertFee,
    setExpertFeeField,
    removeExpertFee,
    // Multi-org Xero: pick which organisation this order is invoiced into.
    xeroOrganisations,
    xeroConnectionId,
    onXeroOrgChange,
    // A pending org switch awaiting confirmation, and its outcome. Rendered by
    // SwitchXeroOrgModal below — the switch voids a live Xero document, so it is
    // confirmed in-app rather than through a browser pop-up.
    xeroOrgSwitch,
    confirmXeroOrgSwitch,
    cancelXeroOrgSwitch,
    // Which of the client's saved addresses this invoice is billed to.
    xeroAddressId,
    onXeroAddressChange,
    // Which branding theme (Xero letterhead) the invoice is generated with.
    xeroBrandingThemeId,
    onXeroBrandingChange,
    // Bumped by om when a pending STRIPE LINK is settled by Stripe's webhook
    // while this page is open. The row itself is patched in om; this is what
    // makes the GENERATED LINKS table's STATUS column follow.
    linkSettleCount,
    // Invoiced natively — no Xero bank account to pick on any row.
    nativeInvoice,
  } = useOrderManagement();
  const { settings } = useContext(SettingsContext);
  const { user } = useContext(UserContext) || {};
  const [markAsPaidState, setMarkAsPaidState] = useState("idle");

  // Every footer button here calls savePayments — Add to cart, Save deposits,
  // Save Payments, Review Payments, Mark as Paid. From "Master Store" there is
  // no single store to write the order/payment against, so the whole cluster is
  // disabled with an explanation rather than a subset of the labels.
  const { blocked: storeGuardBlocked, reason: storeGuardReason } = useStoreWriteGuard();

  // ── Remove payment ─────────────────────────────────────────────────────────
  // Gated on the `remove_payment` capability. The action ONLY updates the order
  // + Xero invoice (it does not move money) — the row's modal makes the rep
  // confirm the refund happened out-of-band first.
  const canRemovePayment = canAdminAccess(user, ACCESS.REMOVE_PAYMENT);
  const { removePayment } = useRemovePayment();

  // Performs the DELETE, then reflects the removal in local order state so the
  // PAYMENTS totals + PX Summary (which read om.paidAmount, derived from these
  // rows) re-compute immediately — no manual Save. `cleanup` drops the specific
  // row that was removed. Errors propagate to the modal (metadata.silent on the
  // request means this is the only place the server message is shown).
  const handleRemovePayment = async (paymentId) => {
    if (!paymentId) return;
    await removePayment({ paymentId, refundConfirmed: true });
    // Reload so the whole order reflects the removal: the payment rows AND the
    // Order Summary "Status", which is server-derived (request.status) and
    // can't be patched from local state. Local-state cleanup proved unreliable
    // for the TRP row, so a hard reload is the robust choice for this rare,
    // deliberate admin action.
    window.location.reload();
  };

  // ── Generated Links section ──────────────────────────────────────────────
  // Lists every PaymentLink created against this order so the rep can copy
  // a previously-generated URL or revoke a pending one. Refetched on order
  // load and whenever a row triggers refreshLinks() after Generate/Revoke.
  const [generatedLinks, setGeneratedLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);

  const fetchLinks = useCallback(async () => {
    if (!order?.id) {
      setGeneratedLinks([]);
      return;
    }
    setLinksLoading(true);
    try {
      const { data } = await axios.get(
        `/api/admin/orders/${order.id}/payment-links`
      );
      setGeneratedLinks(Array.isArray(data) ? data : data?.data || []);
    } catch {
      // Backend may not be deployed on this env, or order has no links.
      setGeneratedLinks([]);
    } finally {
      setLinksLoading(false);
    }
    // linkSettleCount: refetch when om reports a link was paid, so the STATUS
    // column flips from pending to paid with no reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, linkSettleCount]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const copyLink = async (url) => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); } catch { /* noop */ }
  };

  const revokeLink = async (linkId) => {
    if (!window.confirm("Revoke this payment link? The customer will no longer be able to pay.")) return;
    try {
      await axios.post(`/api/admin/payment-links/${linkId}/revoke`);
      fetchLinks();
    } catch (e) {
      window.alert(e?.response?.data?.message || "Failed to revoke link.");
    }
  };

  // Whole-unit money (rounded, no decimals) — delegates to the shared
  // multi-currency formatter; the symbol follows the currency code.
  const formatLinkMoney = (pence, currency = "GBP") =>
    formatMoney(pence, { currency, decimals: 0 });

  const formatLinkDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (lastStep < STEPS.PAYMENTS) {
    return null;
  }

  const dueAmount = toPay - paidAmount;

  // The paid totals below are denominated in `listingRate` — om converts every
  // row into the deal currency before summing — and are labelled with it, so
  // amount and currency do travel together here. What's invisible is the
  // restatement itself: on order 87362 the client paid €26,850 and this section
  // only ever said "£22,976.35". Surface the ORIGINAL alongside it, summed
  // straight off the rows with no conversion at all (they share one currency,
  // so there is nothing to convert). Display-only; no total or routing changes.
  const paidCurrencies = new Set((paymentRows ?? []).map((r) => r.currency));
  const paidOriginal =
    paidCurrencies.size === 1 &&
    !paidCurrencies.has(String(listingRate || "").toLowerCase())
      ? {
          amount: (paymentRows ?? []).reduce((sum, r) => sum + r.amount, 0),
          currency: [...paidCurrencies][0],
        }
      : null;
  const collapsibleOpenProps = {
    open: step === STEPS.PAYMENTS,
    handleTriggerClick: isInline
      ? () => setStep(step === STEPS.PAYMENTS ? -1 : STEPS.PAYMENTS)
      : undefined,
  };

  const showMarkAsPaidByDao = isPxOrder && !fxMissing && toPay < 0;

  const onMarkAsPaidByDao = async () => {
    if (markAsPaidState !== "idle") return;
    setMarkAsPaidState("loading");
    try {
      // If the cart order hasn't been created yet, create it first (same
      // path as "Add to cart") so we have an order_id to mark as paid.
      let targetOrderId = order?.id;
      if (!targetOrderId) {
        const newOrder = await createNewUnpaidOrder();
        targetOrderId = newOrder?.id;
      }
      if (!targetOrderId) {
        setMarkAsPaidState("idle");
        return;
      }
      const marked = await axios.post(
        `/api/order-management/${targetOrderId}/mark-as-paid-by-dao`
      );
      // A 5xx resolves undefined (global interceptor): not paid — let the
      // rep try again instead of showing "done".
      if (!marked) {
        setMarkAsPaidState("idle");
        return;
      }
      setMarkAsPaidState("done");
    } catch (e) {
      setMarkAsPaidState("idle");
    }
  };

  return (
    <>
      <Collapsible
        trigger={<SectionTrigger icon="payments" label="Payments" />}
        {...collapsibleOpenProps}
      >
        <div className="order-payments mt-4">
          <div className="xero-doc-selects">
            {/* Xero organisation — which connected org this invoice, its contact
                and payments are created in. Locked once an invoice exists so the
                order can't be split across organisations. */}
            <XeroOrgSelect
              organisations={xeroOrganisations}
              value={xeroConnectionId}
              onChange={onXeroOrgChange}
            />

            {/* Voiding and re-issuing a live invoice is not a dropdown-sized
                decision, so it is confirmed in a themed dialog that names both
                organisations — and that owns the outcome, including Xero's
                reason when it refuses. */}
            {xeroOrgSwitch && (
              <SwitchXeroOrgModal
                open
                documentLabel="invoice"
                documentNumber={xeroOrgSwitch.invoiceNumber}
                fromLabel={orgLabel(xeroOrganisations, xeroOrgSwitch.fromId)}
                toLabel={orgLabel(xeroOrganisations, xeroOrgSwitch.toId)}
                submitting={xeroOrgSwitch.submitting}
                error={xeroOrgSwitch.error}
                missingInvoice={xeroOrgSwitch.missingInvoice}
                onCancel={cancelXeroOrgSwitch}
                onConfirm={confirmXeroOrgSwitch}
              />
            )}

            {/* The branding theme (letterhead) the invoice is generated with,
                beside the org it belongs to. Defaults to that org's default
                theme; changing it re-issues the invoice under the new one on the
                next GO TO INVOICE. */}
            <XeroBrandingSelect
              xeroConnectionId={xeroConnectionId}
              value={xeroBrandingThemeId}
              onChange={onXeroBrandingChange}
            />
          </div>

          {/* Which of the client's saved addresses this invoice is billed to.
              Pushed onto their Xero contact when the invoice is generated, so
              it appears on the PDF. Null = the client's default address. */}
          <XeroAddressSelect
            userId={order?.user_id ?? order?.user?.id}
            value={xeroAddressId}
            onChange={onXeroAddressChange}
          />

          {(listings || []).map((listing, idx) => (
            <div key={listing.id}>
              <div className="title mt-5 mb-4">
                Listing {idx + 1} |&nbsp;
                {listing.product?.brand?.name} {listing.product?.model.name}{" "}
                |&nbsp;
                {listing.product?.reference_number} |&nbsp;
                {/* The listing's price in the LISTING's OWN currency — amount
                    and currency off the same record, matching the Order Summary
                    card's cart line. Labelling it with the deal currency
                    (`listingRate`) is what showed order 87362's £23,500 listing
                    as "€23,500"; a listing with no currency of its own is legacy
                    GBP, never the deal's. When this disagrees with TOTAL LISTING
                    PRICE below (which mirrors order.total_amount, built from raw
                    listing prices and stamped with the deal currency), the
                    listing and the quote that sold it really are in different
                    currencies — the card's amber notice explains it. */}
                <Money
                  amount={listing.price}
                  className="title"
                  currency={listingPriceCurrency(listing)}
                />
              </div>

              <div className="strong-label mb-4">Deposit Paid:</div>

              {deposits[listing.id]?.map((deposit, idx) => {
                // Callback for when a Stripe Link is generated from this row.
                // Writes the multiple fields back into the row state so the
                // next render shows "Awaiting Payment" + locks the amount.
                const applyLinkResponse = (fields) => {
                  Object.entries(fields).forEach(([k, v]) => {
                    updateDepositField(listing.id, idx, k, v);
                  });
                  // Pull the new link into the Generated Links section
                  // at the bottom without waiting for a page reload.
                  fetchLinks();
                };
                // Callback for when an admin revokes a pending link from
                // the row — clears the link state so the rep can re-generate
                // (or change the row's amount before regenerating).
                const onLinkRevoked = () => {
                  applyLinkResponse({
                    payment_link_status: "revoked",
                    payment_status: "cancelled",
                  });
                };
                return (
                  <PaymentRow
                    key={deposit.key}
                    idx={idx}
                    paymentStatus={deposit.payment_status}
                    setPaymentStatus={(value) =>
                      updateDepositField(
                        listing.id,
                        idx,
                        "payment_status",
                        value
                      )
                    }
                    amount={deposit.amount}
                    setAmount={(value) =>
                      updateDepositField(listing.id, idx, "amount", value)
                    }
                    currency={deposit.currency}
                    setCurrency={(value) =>
                      updateDepositField(listing.id, idx, "currency", value)
                    }
                    date={deposit.date}
                    setDate={(value) =>
                      updateDepositField(listing.id, idx, "date", value)
                    }
                    method={deposit.method}
                    setMethod={(value) =>
                      updateDepositField(listing.id, idx, "method", value)
                    }
                    cardType={deposit.card_type}
                    setCardType={(value) =>
                      updateDepositField(listing.id, idx, "card_type", value)
                    }
                    fee={deposit.fee}
                    setFee={(value) =>
                      updateDepositField(listing.id, idx, "fee", value)
                    }
                    bankAccountId={deposit.bank_account_id}
                    setBankAccountId={(value) =>
                      updateDepositField(
                        listing.id,
                        idx,
                        "bank_account_id",
                        value
                      )
                    }
                    showErrors={showPaymentErrors}
                    hideBankAccount={nativeInvoice}
                    xeroConnectionId={xeroConnectionId}
                    // STRIPE LINK row context
                    order={order}
                    orderItemId={deposit.order_item_id}
                    paymentId={deposit.payment_id}
                    paymentLinkId={deposit.payment_link_id}
                    paymentLinkStatus={deposit.payment_link_status}
                    onLinkGenerated={applyLinkResponse}
                    onLinkRevoked={onLinkRevoked}
                    // POS writes the same fields back as the link flow
                    // (payment_id / payment_status / date / card_type).
                    onPosUpdate={applyLinkResponse}
                    // Remove payment — fully drop the deposit row so its stale
                    // payment_id is discarded (a re-entered deposit gets a fresh
                    // payment) and paidAmount / totals recompute at once. An
                    // empty input row is kept for immediate re-entry.
                    canRemovePayment={canRemovePayment}
                    onRemove={(pid) =>
                      handleRemovePayment(pid, () =>
                        removeDepositRow(listing.id, idx)
                      )
                    }
                  />
                );
              })}

              <TaxRate
                taxRate={taxRates?.listings?.[listing.id]}
                setTaxRate={(rate) =>
                  updateTaxRate("listings", listing.id, rate, listing.id)
                }
              />
            </div>
          ))}

          {sellRequests?.map((request, idx) => (
            <div key={request.id}>
              <div className="title mt-5 mb-4">
                Sell Request {idx + 1} |&nbsp;
                {request.product?.brand?.name} {request.product?.model.name}{" "}
                |&nbsp;
                {request.product?.reference_number} |&nbsp;
                {/* The selected sell quote's price in the QUOTE's own currency.
                    A quote with no currency stored is legacy GBP — falling back
                    to the deal currency here paired one record's amount with
                    another record's currency. */}
                <Money
                  amount={quotes[request.id]?.price}
                  className="title"
                  currency={quotes[request.id]?.currency || "gbp"}
                />
              </div>

              <TaxRate
                taxRate={taxRates?.requests?.[request.id]}
                setTaxRate={(rate) =>
                  updateTaxRate(
                    "requests",
                    request.id,
                    rate,
                    request.listing_id
                  )
                }
              />
            </div>
          ))}

          <Separator />

          <div className="strong-label mt-5 mb-4">Total Remaining Paid</div>

          <PaymentRow
            paymentStatus={totalRemainingPaid.payment_status}
            setPaymentStatus={(value) =>
              setTotalRemainingPaidField("payment_status", value)
            }
            amount={totalRemainingPaid.amount}
            setAmount={(value) => setTotalRemainingPaidField("amount", value)}
            currency={totalRemainingPaid.currency}
            setCurrency={(value) =>
              setTotalRemainingPaidField("currency", value)
            }
            date={totalRemainingPaid.date}
            setDate={(value) => setTotalRemainingPaidField("date", value)}
            method={totalRemainingPaid.method}
            setMethod={(value) => setTotalRemainingPaidField("method", value)}
            cardType={totalRemainingPaid.card_type}
            setCardType={(value) =>
              setTotalRemainingPaidField("card_type", value)
            }
            bankAccountId={totalRemainingPaid.bank_account_id}
            setBankAccountId={(value) =>
              setTotalRemainingPaidField("bank_account_id", value)
            }
            fee={totalRemainingPaid.fee}
            setFee={(value) => setTotalRemainingPaidField("fee", value)}
            showErrors={showPaymentErrors}
            hideBankAccount={nativeInvoice}
            xeroConnectionId={xeroConnectionId}
            // STRIPE LINK row context for TRP: same shape as the deposit
            // rows above. Without these, canGenerate flunks the !!order?.id
            // check and the Generate Link button never renders for a TRP
            // STRIPE LINK saved as a draft Payment.
            order={order}
            paymentId={totalRemainingPaid.payment_id}
            paymentLinkId={totalRemainingPaid.payment_link_id}
            paymentLinkStatus={totalRemainingPaid.payment_link_status}
            onLinkGenerated={(fields) => {
              Object.entries(fields).forEach(([k, v]) => {
                setTotalRemainingPaidField(k, v);
              });
              fetchLinks();
            }}
            onLinkRevoked={() => {
              setTotalRemainingPaidField("payment_link_status", "revoked");
              setTotalRemainingPaidField("payment_status", "cancelled");
              fetchLinks();
            }}
            // POS writes the same fields back as the link flow for the TRP row.
            onPosUpdate={(fields) => {
              Object.entries(fields).forEach(([k, v]) => {
                setTotalRemainingPaidField(k, v);
              });
            }}
            // Remove payment — reset the TRP row so it drops out of paidAmount
            // and the "Paid" pill (and the button) clears.
            canRemovePayment={canRemovePayment}
            onRemove={(pid) =>
              handleRemovePayment(pid, () => {
                setTotalRemainingPaidField("amount", 0);
                setTotalRemainingPaidField("payment_id", null);
                setTotalRemainingPaidField("payment_status", "created");
              })
            }
          />

          {/* Match the spacing pattern used by other section headings
              (TOTAL REMAINING PAID, TAX RATE): mb-4 between heading and
              the first field below, mt-4 above the heading itself
              instead of mt-5. */}
          <div className="strong-label mt-4 mb-4">Marketplace Fee:</div>

          {/* Aligned flush-left with the start of the Total Remaining Paid
              PaymentRow above — the old 110px spacer that mirrored the
              status-pill column has been removed. mb-2 keeps the gap
              before the next section tight. */}
          <div className="d-flex mb-2" style={{ gap: "14px", alignItems: "center" }}>
            <div className="card-type-container bs-5">
              <MarketplaceFeeType
                value={marketplaceFeeType}
                onChange={setMarketplaceFeeType}
                readOnly={false}
              />
            </div>

            <div className="amount">
              <div className="marketplace-fee-field">
                <FieldGroup
                  type="numberFormat"
                  name="marketplace_fee"
                  persistLabel
                  inputStyle={{
                    paddingLeft: marketplaceFee ? "30px" : "10px",
                    width: "120px",
                    paddingTop: "4px",
                  }}
                  // The fee is entered as a bare number and om treats it as DEAL
                  // currency money (it folds it into paidAmount and converts it
                  // with sellingToGbpRate for profit), so the symbol shown must
                  // be the deal currency's. The store-base symbol invited the
                  // rep to type £ on a € order — a mislabel that then flows into
                  // paidAmount, Net Profit and order.marketplace_fee.
                  placeholder={marketplaceFee ? symbolFor(listingRate) : ""}
                  noFormik
                  value={marketplaceFee}
                  onChange={setMarketplaceFee}
                />
              </div>
            </div>
          </div>

          {/* mt-4 keeps the gap before "Left to Pay" in line with the
              other inter-section gaps in this view. */}
          <div className="row mt-4">
            <div className="col-12 col-lg-8">
              <div className="label-value-row">
                {isPxOrder ? (
                  <>
                    <div className="strong-label">Left to Pay:</div>
                    <div className="value">
                      {fxMissing ? (
                        <FxUnavailable unavailable={fxUnavailable} />
                      ) : (
                        <Money amount={toPay} currency={listingRate} />
                      )}
                    </div>
                    {!fxMissing && (
                      <>
                        <div className="strong-label mx-3">by</div>
                        <div className="value">
                          {toPay >= 0 ? "client" : (settings?.name || "Store")}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="strong-label">
                      Total Listing{listings.length > 1 ? "s" : ""} Price:
                    </div>
                    <div className="value">
                      {fxMissing ? (
                        <FxUnavailable unavailable={fxUnavailable} />
                      ) : (
                        <Money amount={toPay} currency={listingRate} />
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="label-value-row">
                <div className="strong-label">Total Paid By Client:</div>
                <div className="value">
                  {fxMissing && <FxUnavailable unavailable={fxUnavailable} />}
                  {!fxMissing && <Money amount={paidAmount} zero currency={listingRate} />}
                  {paidOriginal && (
                    <>
                      &nbsp;(paid{" "}
                      {formatMoney(paidOriginal.amount, {
                        currency: paidOriginal.currency,
                      })}
                      )
                    </>
                  )}
                  {!fxMissing && paidAmount && paidAmount !== toPay && (
                    <>
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; (Due:{" "}
                      <Money amount={dueAmount} zero currency={listingRate} />)
                    </>
                  )}
                </div>
              </div>

              <div className="label-value-row">
                <div className="strong-label">Total Fees:</div>
                <div className="value">
                  {fxMissing ? (
                    <FxUnavailable unavailable={fxUnavailable} />
                  ) : (
                    <Money
                      amount={-fees.card - fees.bank}
                      zero
                      currency={listingRate}
                    />
                  )}
                </div>
              </div>

              <div className="label-value-row">
                <div className="strong-label">Total Paid After Fees:</div>
                <div className="value">
                  {fxMissing ? (
                    <FxUnavailable unavailable={fxUnavailable} />
                  ) : (
                    <Money
                      amount={paidAmount - fees.card - fees.bank}
                      zero
                      currency={listingRate}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* EXPERT FEES — an optional amount the client paid ON TOP of the
              watch for a merchant-defined expert (an authenticator, watchmaker,
              consultant). It sits below the paid totals because it is tracked
              apart from them: it never enters Total Paid By Client, Total Fees
              or Total Paid After Fees above, and it never reaches the Xero
              invoice. It is added to the deal's profit before commissions are
              calculated, but only once BOTH an amount and an expert are set. */}
          {/* Heading carries the Remove action, so it is reachable even when
              the row itself is locked (payment_status=completed makes every
              field read-only). Shown once there is anything to remove —
              a saved record, or values typed but not yet saved. */}
          <div className="expert-fee-heading mt-5 mb-4">
            <span className="strong-label">Expert Fees</span>
            {(!!expertFee.id ||
              !!expertFee.payment_id ||
              !!parseFloat(expertFee.amount || 0) ||
              !!expertFee.expert_account_id) && (
              <Button
                variant="danger"
                size="sm"
                data-btn="payment-delete-expert"
                onClick={async () => {
                  if (
                    !window.confirm(
                      "Remove this expert fee? It will no longer count towards profit or commissions."
                    )
                  )
                    return;
                  await removeExpertFee();
                }}
              >
                Remove
              </Button>
            )}
          </div>

          <PaymentRow
            paymentStatus={expertFee.payment_status}
            setPaymentStatus={(value) =>
              setExpertFeeField("payment_status", value)
            }
            amount={expertFee.amount}
            setAmount={(value) => setExpertFeeField("amount", value)}
            currency={expertFee.currency}
            setCurrency={(value) => setExpertFeeField("currency", value)}
            date={expertFee.date}
            setDate={(value) => setExpertFeeField("date", value)}
            method={expertFee.method}
            setMethod={(value) => setExpertFeeField("method", value)}
            cardType={expertFee.card_type}
            setCardType={(value) => setExpertFeeField("card_type", value)}
            // No FX / card fee on this row — the amount is the whole figure.
            hideFee
            // TRANSFER / CARD / CASH / USDT — the same set Commissions uses.
            // Stripe Link and POS collect money FROM a customer, which is the
            // opposite direction to this row.
            allowedMethods={MANUAL_PAYMENT_METHODS}
            // Replaces the bank-account dropdown entirely: an expert is a name
            // the merchant maintains, not a Xero ledger account, and this row
            // is never posted against an invoice.
            accountField={
              <ExpertAccountSelect
                value={expertFee.expert_account_id}
                onChange={(value, option) => {
                  setExpertFeeField("expert_account_id", value);
                  // Kept alongside the id so the Order Summary card can name
                  // the expert without re-fetching the list.
                  setExpertFeeField("expert_account_name", option?.name ?? null);
                }}
                // Follows the row's lock, like every other field: a confirmed
                // fee shouldn't let you change WHO was paid while the amount
                // stays read-only. Editing is reachable by toggling the
                // padlock; deleting is reachable via Remove above, which
                // ignores the lock entirely.
                disabled={expertFee.payment_status === "completed"}
              />
            }
            // No showErrors: the whole record is optional, so an empty or
            // half-filled row must never block Save Payments / Go To Invoice.
          />

          {/* Says plainly why the figure hasn't moved anything yet — an amount
              with no expert is an incomplete record and is ignored by profit. */}
          {!!parseFloat(expertFee.amount || 0) &&
            !expertFee.expert_account_id && (
              <div className="expert-fee-hint">
                Select an expert for this fee to count towards profit and
                commissions.
              </div>
            )}

          {/* Generated Links — only shown once the order exists (an order
              is needed for links to attach to). Sits between the totals
              and the action buttons with generous spacing on both sides. */}
          {order?.id && (
            <div className="generated-links mt-5 mb-4">
              <div className="strong-label mb-3">Generated Links</div>
              {linksLoading ? (
                <div className="generated-links__empty">Loading…</div>
              ) : generatedLinks.length === 0 ? (
                <div className="generated-links__empty">
                  No links generated yet.
                </div>
              ) : (
                <div className="generated-links__table">
                  <div className="generated-links__row generated-links__row--header">
                    <div className="generated-links__cell generated-links__cell--amount">Amount</div>
                    <div className="generated-links__cell generated-links__cell--status">Status</div>
                    <div className="generated-links__cell generated-links__cell--date">Created</div>
                    <div className="generated-links__cell generated-links__cell--email">Customer</div>
                    <div className="generated-links__cell generated-links__cell--label">Label</div>
                    <div className="generated-links__cell generated-links__cell--actions" />
                  </div>
                  {generatedLinks.map((link) => (
                    <div className="generated-links__row" key={link.id}>
                      <div className="generated-links__cell generated-links__cell--amount">
                        {formatLinkMoney(link.amount, link.currency)}
                      </div>
                      <div className="generated-links__cell generated-links__cell--status">
                        <span className={`generated-links__dot generated-links__dot--${link.status}`} />
                        {link.status}
                      </div>
                      <div className="generated-links__cell generated-links__cell--date">
                        {formatLinkDate(link.created_at)}
                      </div>
                      <div className="generated-links__cell generated-links__cell--email">
                        {link.email}
                      </div>
                      <div className="generated-links__cell generated-links__cell--label">
                        {link.label || "—"}
                      </div>
                      <div className="generated-links__cell generated-links__cell--actions">
                        {link.status === "pending" && link.url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            data-btn="payment-duplicate-link"
                            onClick={() => copyLink(link.url)}
                          >
                            Copy
                          </Button>
                        )}
                        {link.status === "pending" && (
                          <Button
                            variant="danger"
                            size="sm"
                            data-btn="payment-delete-link"
                            onClick={() => revokeLink(link.id)}
                          >
                            Revoke
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="error-line mt-3">
            {showPaymentErrors && !arePaymentsValid && (
              <>Not all required fields are filled in.</>
            )}
          </div>

          <div className="action-buttons text-right payment-buttons mt-2">
            {/*
              Button label/routing was originally keyed on `paidAmount`
              ("money actually collected"). When STRIPE LINK rows were
              added (Path A), drafts were excluded from `paidAmount` —
              correct for the "Total Paid By Client" display, but it
              regressed this button: a stripe_link-only row left
              `paidAmount === 0` so the bottom action mis-rendered as
              "Add to cart" and routed through createNewUnpaidOrder
              (which never persists the draft).
              We now key the LABEL/ROUTING on `populatedAmount` (sum
              including drafts) so populated rows surface "Save deposits"
              / "Save Payments" / "Mark as Paid" regardless of method.
              The FINALITY conditions (Save vs Mark as Paid) still gate
              on `paidAmount === toPay` because Mark as Paid only makes
              sense when the customer has actually paid — a stripe_link
              draft hasn't been collected yet, so it must remain on
              "Save Payments" until the webhook lands.
            */}
            {!populatedAmount && (
              <Button variant="primary" data-btn="payment-add" onClick={savePayments} disabled={storeGuardBlocked} title={storeGuardReason}>
                Add to cart
              </Button>
            )}
            {!!populatedAmount && populatedAmount < toPay && !totalRemainingPaid.amount && (
              <Button variant="primary" data-btn="payment-save-deposits" onClick={savePayments} disabled={storeGuardBlocked} title={storeGuardReason}>
                Save deposits
              </Button>
            )}
            {!!populatedAmount && populatedAmount < toPay && !!totalRemainingPaid.amount && (
              <Button variant="primary" data-btn="payment-save-review" onClick={savePayments} disabled={storeGuardBlocked} title={storeGuardReason}>
                Review payments
              </Button>
            )}
            {!!populatedAmount &&
              populatedAmount >= toPay &&
              !!totalRemainingPaid.amount &&
              !allPaymentsConfirmed && (
                <Button variant="primary" data-btn="payment-save" onClick={savePayments} disabled={storeGuardBlocked} title={storeGuardReason}>
                  Save payments
                </Button>
              )}
            {!!populatedAmount &&
              populatedAmount >= toPay &&
              !!totalRemainingPaid.amount &&
              allPaymentsConfirmed && (
                <Button
                  variant="primary"
                  data-btn="payment-confirm-paid"
                  onClick={savePayments}
                  disabled={storeGuardBlocked}
                  title={storeGuardReason}
                >
                  Mark as paid
                </Button>
              )}

            <Button
              variant="secondary"
              data-btn="payment-open-invoice"
              onClick={() => {
                // If no order exists yet (zero-payments scenario where
                // admin proceeds straight to the invoice without
                // touching deposits), savePayments routes through
                // createNewUnpaidOrder which itself calls goToInvoice
                // after the order is created. Otherwise jump directly.
                if (!order?.id) {
                  savePayments();
                } else {
                  goToInvoice();
                }
              }}
            >
              Go to invoice
            </Button>
            {showMarkAsPaidByDao && (
              <Button
                variant="secondary"
                data-btn="payment-confirm-dao"
                onClick={onMarkAsPaidByDao}
                disabled={markAsPaidState === "done"}
                loading={markAsPaidState === "loading"}
              >
                {markAsPaidState === "done" ? "Marked as paid" : "Mark as paid"}
              </Button>
            )}
          </div>
        </div>
      </Collapsible>

      <ReactModal
        {...{
          isOpen: paymentDialog.open,
          onRequestClose: () =>
            updatePaymentDialog(PAYMENT_DIALOG.DECISIONS.CANNOT_DECIDE),
          portalClassName: "payment-dialog-portal",
          style: {
            content: {
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              marginRight: "-50%",
              transform: "translate(-50%, -50%)",
              borderRadius: "5px",
              border: "none",
            },
          },
        }}
      >
        <div className="payment-dialog">
          <Alert
            type="warning"
            message={
              paymentDialog.step === PAYMENT_DIALOG.STEPS.QUESTION
                ? "Action needed"
                : "Confirmation needed"
            }
          >
            <p>
              {!paymentDialog.decision && paidAmount !== toPay && (
                <>
                  Total Paid: <Money amount={paidAmount} currency={listingRate} />{" "}
                  is not equal to
                  {isPxOrder ? " Left to Pay" : " Listing Price"}:{" "}
                  <Money amount={toPay} currency={listingRate} />.
                </>
              )}

              {paymentDialog.decision ===
                PAYMENT_DIALOG.DECISIONS.UPDATE_LISTING_PRICE && (
                <>
                  Listing Price will be updated to{" "}
                  <Money amount={newListingPrice} currency={listingRate} />{" "}
                  and the listing will be saved in{" "}
                  {String(listingRate).toUpperCase()}.
                </>
              )}
            </p>
          </Alert>
          {/* ButtonGroup, not a scss gap: .payment-dialog is shared with
              StepPayments, which renders the same ButtonGroup row. */}
          <ButtonGroup align="end" className="action-buttons mt-4">
            {!paymentDialog.decision ? (
              <>
                <Button
                  variant="secondary"
                  data-btn="payment-cancel-dialog"
                  onClick={() =>
                    updatePaymentDialog(PAYMENT_DIALOG.DECISIONS.CANNOT_DECIDE)
                  }
                >
                  I will fix payment records
                </Button>
                {!!dueAmount && listings.length === 1 && (
                  <Button
                    variant="primary"
                    data-btn="payment-edit-price"
                    onClick={() =>
                      updatePaymentDialog(
                        PAYMENT_DIALOG.DECISIONS.UPDATE_LISTING_PRICE
                      )
                    }
                  >
                    Update listing price
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  data-btn="payment-cancel-back"
                  onClick={() => updatePaymentDialog(null)}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  data-btn="payment-confirm-dialog"
                  onClick={() =>
                    updatePaymentDialog(PAYMENT_DIALOG.DECISIONS.CONFIRM)
                  }
                >
                  Confirm
                </Button>
              </>
            )}
          </ButtonGroup>
        </div>
      </ReactModal>
    </>
  );
}
